// Buscas dos indicadores novos do Financeiro e do Faturamento (#6506): régua em dia, acordos
// cumpridos, especialidade da Cobrança e quem entra e sai do rateio. A regra de cada número
// está em src/lib/visaoGeral/cobranca.ts e backoffice.ts.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { fetchEsteira } from '../../src/services/esteiraCobranca';
import type { DemandaEsteira } from '../../types';
import type { AcaoComExecucao, BoletoAEnviar, ParcelaDoAcordo } from '../../src/lib/visaoGeral/cobranca';
import type { AprovadaParaRateio, CancelamentoConcluido, ItemDaPlanilha } from '../../src/lib/visaoGeral/backoffice';
import { listarRateios, carregarItensRateio } from '../Gestao/Rateio/saldosUcsDados';

const PAGINA = 1000;
const LOTE = 200;

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

/** `in` em lotes (a URL não comporta milhares de ids). */
async function emLotes<T, I>(ids: I[], buscar: (lote: I[]) => Promise<T[]>): Promise<T[]> {
  const todas: T[] = [];
  for (let i = 0; i < ids.length; i += LOTE) todas.push(...await buscar(ids.slice(i, i + LOTE)));
  return todas;
}

function useBusca<T>(buscar: () => Promise<T>, deps: unknown[], rotulo: string): { dados: T | null; erro: boolean } {
  const [estado, setEstado] = useState<{ dados: T | null; erro: boolean }>({ dados: null, erro: false });
  useEffect(() => {
    let vivo = true;
    setEstado({ dados: null, erro: false });
    buscar()
      .then(dados => { if (vivo) setEstado({ dados, erro: false }); })
      .catch(e => { console.error(`${rotulo}:`, e); if (vivo) setEstado({ dados: null, erro: true }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return estado;
}

const menosDias = (iso: string, dias: number) => {
  const [a, m, d] = iso.split('-').map(Number);
  const dt = new Date(a, m - 1, d - dias);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/**
 * Ações da régua da pessoa (todos os status) que podem cair no período: o vencimento de
 * origem vai até 24 dias antes do início (a D+24 de um boleto vencido no fim do mês anterior).
 */
export const useAcoesDaReguaDoPeriodo = (pessoaId: string, inicioIso: string) => useBusca<AcaoComExecucao[]>(() =>
  paginar<AcaoComExecucao>(() => supabase.from('cobranca_acao_manual')
    .select('id, etapa, data_referencia, status, responsavel_id, executado_por, executado_em, dispensado_motivo')
    .eq('responsavel_id', pessoaId)
    .gte('data_referencia', menosDias(inicioIso, 24))
    .order('id')), [pessoaId, inicioIso], 'Régua em dia');

/** Parcelas dos acordos fechados pela pessoa que vencem no período, com a data do pagamento. */
export const useParcelasDosMeusAcordos = (pessoaId: string, inicioIso: string, fimIso: string) => useBusca<ParcelaDoAcordo[]>(async () => {
  const acordos = await paginar<{ id: string; cliente_nome_snapshot: string | null }>(() => supabase.from('parcelamentos')
    .select('id, cliente_nome_snapshot')
    .eq('criado_por', pessoaId)
    .order('id'));
  if (acordos.length === 0) return [];
  const cliente = new Map(acordos.map(a => [a.id, a.cliente_nome_snapshot]));
  const parcelas = await emLotes(acordos.map(a => a.id), lote => paginar<any>(() => supabase.from('parcelamento_parcelas')
    .select('id, parcelamento_id, data_vencimento, status, fatura_id')
    .in('parcelamento_id', lote)
    .gte('data_vencimento', inicioIso)
    .lte('data_vencimento', fimIso)
    .order('id')));
  const faturaIds = [...new Set(parcelas.map(p => p.fatura_id).filter((v: unknown) => v != null))] as number[];
  const faturas = await emLotes(faturaIds, lote => paginar<any>(() => supabase.from('faturas')
    .select('id, status_boleto_vigor, data_pgto_boleto')
    .in('id', lote)
    .order('id')));
  const pagoEm = new Map(faturas.map(f => [Number(f.id), f.status_boleto_vigor === 'Pago' ? f.data_pgto_boleto : null]));
  return parcelas.map(p => ({
    id: String(p.id),
    data_vencimento: String(p.data_vencimento),
    status: p.status,
    pago_em: p.fatura_id != null ? pagoEm.get(Number(p.fatura_id)) ?? null : null,
    cliente: cliente.get(p.parcelamento_id) ?? null,
  }));
}, [pessoaId, inicioIso, fimIso], 'Acordos cumpridos');

/** Boletos emitidos esperando o envio ao cliente (sem a cobrança agrupada da Prefeitura). */
export const useBoletosAEnviar = () => useBusca<BoletoAEnviar[]>(async () => {
  const linhas = await paginar<any>(() => supabase.from('faturas')
    .select('id, cliente_nome, uc_codigo, mes_referencia, emitido_em, cobranca_agrupada')
    .eq('status_fatura', 'Com boleto emitido / A enviar')
    .eq('ambiente', 'producao')
    .is('excluido_em', null)
    .is('pular_envio_em', null)
    .not('emitido_em', 'is', null)
    .order('id'));
  return linhas.filter(l => !(l.cobranca_agrupada ?? '').trim()) as BoletoAEnviar[];
}, [], 'Boletos a enviar');

/** Boletos enviados pela pessoa no período (para medir o envio em até 1 dia útil). */
export const useEnviosDaPessoa = (nome: string | null, inicio: string, fim: string) => useBusca<{ emitido_em: string | null; enviado_em: string | null }[]>(async () => {
  if (!nome) return [];
  return paginar(() => supabase.from('faturas')
    .select('id, emitido_em, enviado_em')
    .eq('enviado_por_nome', nome)
    .gte('enviado_em', inicio)
    .lt('enviado_em', fim)
    .order('id'));
}, [nome, inicio, fim], 'Envios da pessoa');

/** Pendências da Esteira de Cobrança nos grupos de notificação, documentos e negativação. */
export const useDocumentosDaEsteira = () => useBusca<DemandaEsteira[]>(async () => {
  const demandas = await fetchEsteira();
  return demandas.filter(d => d.grupo === 'notificacoes' || d.grupo === 'documentos' || d.grupo === 'negativacao');
}, [], 'Documentos da Esteira');

/**
 * Base de "quem entra e quem sai do rateio": a planilha mais recente, as negociações
 * aprovadas nos últimos 6 meses (com os números de UC) e os cancelamentos concluídos.
 */
export const useEntraESaiDoRateio = () => useBusca<{ planilha: string; itens: ItemDaPlanilha[]; aprovadas: AprovadaParaRateio[]; cancelamentos: CancelamentoConcluido[] } | null>(async () => {
  const rateios = await listarRateios();
  const atual = rateios[0];
  if (!atual) return null;
  const desde = new Date(Date.now() - 180 * 86_400_000).toISOString();
  const [itens, negs, cancs] = await Promise.all([
    carregarItensRateio(atual.id),
    paginar<any>(() => supabase.from('negociacoes')
      .select('id, cliente_nome, numero_uc, consumo_medio_kwh, analise_cadastral!analise_cadastral_negociacao_id_fkey!inner(decisao_final, decisao_final_at)')
      .eq('status', 'termo_aprovado')
      .in('analise_cadastral.decisao_final', ['Aprovado', 'Aprovado com Ressalvas'])
      .gte('analise_cadastral.decisao_final_at', desde)
      .order('id')),
    paginar<any>(() => supabase.from('solicitacoes_cancelamento')
      .select('id, cliente_nome, uc_numero, uc_id, concluida_em')
      .eq('status', 'concluida')
      .order('id')),
  ]);
  const ucsDasNegs = await emLotes(negs.map(n => Number(n.id)), lote => paginar<any>(() => supabase.from('unidades_consumidoras')
    .select('id, negociacao_id, numero_uc, outro_numero_uc').in('negociacao_id', lote).order('id')));
  const ucsDosCancs = await emLotes(cancs.map(c => c.uc_id).filter((v: unknown) => v != null) as number[], lote => paginar<any>(() => supabase.from('unidades_consumidoras')
    .select('id, numero_uc, outro_numero_uc').in('id', lote).order('id')));
  const porNeg = new Map<number, string[]>();
  for (const u of ucsDasNegs) {
    const k = Number(u.negociacao_id);
    porNeg.set(k, [...(porNeg.get(k) ?? []), u.numero_uc, u.outro_numero_uc].filter(Boolean));
  }
  const porUc = new Map<number, string[]>(ucsDosCancs.map(u => [Number(u.id), [u.numero_uc, u.outro_numero_uc].filter(Boolean)]));
  return {
    planilha: atual.mes_referencia,
    itens: itens.map(i => ({ uc: i.uc, outro_numero_uc: i.outro_numero_uc, status: i.status })),
    aprovadas: negs.map(n => {
      const a = Array.isArray(n.analise_cadastral) ? n.analise_cadastral[0] : n.analise_cadastral;
      return {
        negociacao_id: Number(n.id), cliente: n.cliente_nome, aprovadaEm: a?.decisao_final_at ?? '',
        consumo_kwh: n.consumo_medio_kwh == null ? null : Number(n.consumo_medio_kwh),
        numeros: [n.numero_uc, ...(porNeg.get(Number(n.id)) ?? [])],
      };
    }),
    cancelamentos: cancs.map(c => ({
      id: Number(c.id), cliente: c.cliente_nome, uc_numero: c.uc_numero, concluida_em: c.concluida_em,
      numeros: [c.uc_numero, ...(c.uc_id != null ? porUc.get(Number(c.uc_id)) ?? [] : [])],
    })),
  };
}, [], 'Quem entra e quem sai do rateio');
